import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback } from "react"
import { dataUpdated, VideoMetadata } from "@nova/slice-video"
import {
    selectActiveSidePanelModal,
    selectActiveSidePanelModalIsLoading,
    setActiveSidePanelModalIsLoading,
} from "panels/content/side-panel/slice"
import { CumulativeRGB } from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"

export const videoData = {
    metadata: [] as VideoMetadata,
    colordata: new ArrayBuffer(0),
}

export const localVideoMap: Record<string, LocalVideoPayload & { videoUrl: string }> = {}
export type LocalVideoPayload = {
    videoFile: File
    colorData?: Uint32Array[]
    metadata?: VideoMetadata
    cumulativeRgb: CumulativeRGB
    anomalyIndex: Uint8Array
}

export const useUniversalControl = () => {
    const dispatch = useAppDispatch()
    const activeSidePanelModal = useAppSelector(selectActiveSidePanelModal())
    const activeSidePaneModalIsLoading = useAppSelector(selectActiveSidePanelModalIsLoading())

    /**
     * @param id Flight ID.
     * @returns Whether this layer is loading.
     */
    const isLoading = useCallback(
        (id: string) => {
            return activeSidePanelModal?.id === id && activeSidePaneModalIsLoading
        },
        [activeSidePanelModal, activeSidePaneModalIsLoading]
    )

    const setMetadata = useCallback(
        (metadata?: VideoMetadata, colordata?: Uint8Array) => {
            videoData.colordata = (colordata as unknown as ArrayBuffer) || new ArrayBuffer(0)
            videoData.metadata = metadata || []
            dispatch(dataUpdated())
        },
        [dispatch]
    )

    /**
     * Resets media metadata but does not close the layer.
     * Use this only in container useEffect cleanups.
     */
    const resetMetadata = useCallback(() => {
        videoData.metadata = []
        videoData.colordata = new ArrayBuffer(0)
        dispatch(dataUpdated())
    }, [dispatch])

    const setLoading = useCallback(
        (id: string, loading: boolean) => {
            dispatch(setActiveSidePanelModalIsLoading({ id, loading }))
        },
        [dispatch]
    )

    const addLocalVideo = useCallback((flightId: string, payload: LocalVideoPayload) => {
        const videoUrl = URL.createObjectURL(payload.videoFile)
        localVideoMap[flightId] = { ...payload, videoUrl }
    }, [])

    const getLocalVideo = useCallback((flightId: string) => {
        return localVideoMap[flightId] || {}
    }, [])

    return {
        isLoading,
        resetMetadata,
        setLoading,
        setMetadata,
        addLocalVideo,
        getLocalVideo,
    }
}
